import React, { useMemo, useState } from "react";
import clsx from "clsx";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { ChartType, ChartTypeMenu, useChartType } from "../ChartTypeMenu";
import { OverviewChart } from "../OverviewChart";

export type ActivityRow = {
  date: string;
  inbound: number;
  outbound: number;
  leads: number;
};
type Metric = "messages" | "leads";
const chartTypes: ChartType[] = ["bar", "line", "area", "pie", "donut"];
const dayLabel = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

export const ActivityChart: React.FC<{ rows: ActivityRow[] }> = ({ rows }) => {
  const [metric, setMetric] = useState<Metric>("messages");
  const [type, setType] = useChartType("company-activity", "area", chartTypes);
  const totals = useMemo(
    () =>
      rows.reduce(
        (a, r) => ({
          inbound: a.inbound + r.inbound,
          outbound: a.outbound + r.outbound,
          leads: a.leads + r.leads,
        }),
        { inbound: 0, outbound: 0, leads: 0 },
      ),
    [rows],
  );
  const empty =
    metric === "messages"
      ? totals.inbound + totals.outbound === 0
      : totals.leads === 0;
  const series =
    metric === "messages"
      ? [
          { key: "outbound", label: "Sent", color: "var(--color-primary)" },
          { key: "inbound", label: "Received", color: "var(--color-info)" },
        ]
      : [{ key: "leads", label: "New leads", color: "var(--color-success)" }];
  const circular = type === "pie" || type === "donut";
  return (
    <div className="admin-panel p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Activity · last 30 days
          </h3>
          <p className="mt-0.5 text-2xs text-text-muted">
            {metric === "messages"
              ? `${totals.inbound.toLocaleString()} received · ${totals.outbound.toLocaleString()} sent`
              : `${totals.leads.toLocaleString()} new leads`}
            {circular && " · share of period total"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border bg-bg/40 p-0.5">
            {(
              [
                ["messages", "Messages"],
                ["leads", "Leads"],
              ] as [Metric, string][]
            ).map(([k, l]) => (
              <button
                key={k}
                type="button"
                aria-pressed={metric === k}
                onClick={() => setMetric(k)}
                className={clsx(
                  "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                  metric === k
                    ? "bg-surface text-text-primary shadow-sm"
                    : "text-text-muted hover:text-text-primary",
                )}
              >
                {l}
              </button>
            ))}
          </div>
          <ChartTypeMenu
            label="Company activity"
            value={type}
            onChange={setType}
            allowed={chartTypes}
          />
        </div>
      </div>
      {empty ? (
        <div className="flex h-52 items-center justify-center rounded-lg border border-dashed border-border bg-bg/30 text-center">
          <div>
            <div className="text-sm font-medium text-text-primary">
              Nothing in the last 30 days
            </div>
            <div className="mt-0.5 text-2xs text-text-muted">
              {metric === "messages"
                ? "No WhatsApp messages were sent or received."
                : "No leads were created."}
            </div>
          </div>
        </div>
      ) : (
        <div
          className="h-52 w-full"
          role="img"
          aria-label={`${metric === "messages" ? `${totals.inbound} received and ${totals.outbound} sent messages` : `${totals.leads} leads`} over the last 30 days`}
        >
          {circular ? (
            <OverviewChart
              type={type}
              name={metric === "messages" ? "Messages" : "Leads"}
              data={
                metric === "messages"
                  ? [
                      {
                        label: "Sent",
                        value: totals.outbound,
                        color: "var(--color-primary)",
                      },
                      {
                        label: "Received",
                        value: totals.inbound,
                        color: "var(--color-info)",
                      },
                    ]
                  : rows.map((r) => ({
                      label: dayLabel(r.date),
                      value: r.leads,
                    }))
              }
              center={
                <>
                  <strong className="text-2xl font-semibold">
                    {(metric === "messages"
                      ? totals.inbound + totals.outbound
                      : totals.leads
                    ).toLocaleString()}
                  </strong>
                  <span className="text-xs text-text-muted">{metric}</span>
                </>
              }
            />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={rows}
                margin={{ top: 4, right: 4, left: -18, bottom: 0 }}
                accessibilityLayer
              >
                <CartesianGrid
                  stroke="var(--color-border)"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={dayLabel}
                  tick={{ fontSize: 10, fill: "var(--color-text-muted)" }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={28}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: "var(--color-text-muted)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                  labelFormatter={(v) => dayLabel(String(v))}
                />
                {series.map((s) =>
                  type === "bar" ? (
                    <Bar
                      key={s.key}
                      dataKey={s.key}
                      name={s.label}
                      fill={s.color}
                      maxBarSize={20}
                      radius={[3, 3, 0, 0]}
                      isAnimationActive={false}
                    />
                  ) : type === "line" ? (
                    <Line
                      key={s.key}
                      dataKey={s.key}
                      name={s.label}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ) : (
                    <Area
                      key={s.key}
                      dataKey={s.key}
                      name={s.label}
                      stackId="activity"
                      stroke={s.color}
                      fill={s.color}
                      fillOpacity={0.18}
                      strokeWidth={2}
                      isAnimationActive={false}
                    />
                  ),
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
      {metric === "messages" && !empty && (
        <div className="mt-3 flex items-center gap-4 text-2xs text-text-muted">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: s.color }}
              />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
